// @ts-nocheck
/**
 * Structural validation for template.itemUiActions and element.itemUiActionsId (DEC-070).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue } from '../../diagnostics';
import {
  CONTAIND_MENU_BASICS,
  LIST_MENU_BASICS,
  PLACEHOLDER_MENU_BASICS,
  RUNTIME_INJECTED_ACTION_IDS,
  type BuiltinActionId,
  type MenuTreeNode,
} from '../../types/itemUiActions';

const RUNTIME_INJECTED = new Set<string>(RUNTIME_INJECTED_ACTION_IDS);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function collectBuiltinActionIds(nodes: MenuTreeNode[] | undefined, into: Set<string>): void {
  if (!nodes) return;
  for (const node of nodes) {
    if (node.kind === 'builtin') {
      into.add(node.actionId);
    } else if (node.kind === 'submenu') {
      collectBuiltinActionIds(node.children as MenuTreeNode[], into);
    }
  }
}

function collectNodeIds(nodes: MenuTreeNode[] | undefined, into: Set<string>, result: ValidationResult, path: string): ValidationResult {
  if (!nodes) return result;
  let next = result;
  for (const node of nodes) {
    if (typeof node.id !== 'string' || node.id.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'Menu tree node id must be a non-empty string',
        path,
      });
      continue;
    }
    if (into.has(node.id)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.duplicate_id',
        message: `Duplicate menu tree node id '${node.id}'`,
        path: `${path}.${node.id}`,
      });
    }
    into.add(node.id);
    if (node.kind === 'submenu') {
      next = collectNodeIds(node.children as MenuTreeNode[], into, next, path);
    }
    if (node.kind === 'builtin' && RUNTIME_INJECTED.has(node.actionId)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Runtime-injected action '${node.actionId}' must not appear in authored trees`,
        path: `${path}.${node.id}`,
      });
    }
  }
  return next;
}

function warnMissingBasics(
  result: ValidationResult,
  actionIds: Set<string>,
  basics: readonly BuiltinActionId[],
  path: string,
  surface: string,
): ValidationResult {
  let next = result;
  for (const basic of basics) {
    if (!actionIds.has(basic)) {
      next = addIssue(next, {
        severity: 'warning',
        code: 'schema.item_ui_actions_missing_basic',
        message: `${surface} menu is missing basic action '${basic}' (runtime will re-inject)`,
        path,
      });
    }
  }
  return next;
}

function warnMissingDelete(
  result: ValidationResult,
  actionIds: Set<string>,
  path: string,
): ValidationResult {
  const hasDelete =
    actionIds.has('list.delete') ||
    actionIds.has('containd.delete') ||
    actionIds.has('placeholder.delete');
  if (!hasDelete) {
    return addIssue(result, {
      severity: 'warning',
      code: 'schema.item_ui_actions_no_delete',
      message:
        'Authored menus omit delete builtins; ensure another escape hatch remains available',
      path,
    });
  }
  return result;
}

export function collectItemUiActionsIds(
  catalog: readonly { id?: string }[] | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const entry of catalog ?? []) {
    if (typeof entry.id === 'string' && entry.id.trim() !== '') {
      ids.add(entry.id);
    }
  }
  return ids;
}

export function validateItemUiActionsCatalog(
  catalog: unknown,
  result: ValidationResult,
): ValidationResult {
  if (catalog === undefined) return result;
  if (!Array.isArray(catalog)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'itemUiActions must be an array when present',
      path: 'itemUiActions',
    });
  }

  let next = result;
  const seen = new Set<string>();
  catalog.forEach((entry, index) => {
    const path = `itemUiActions[${index}]`;
    if (!isPlainObject(entry)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'itemUiActions entry must be an object',
        path,
      });
      return;
    }
    const id = entry.id;
    if (typeof id !== 'string' || id.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'itemUiActions entry id must be a non-empty string',
        path: `${path}.id`,
      });
      return;
    }
    if (seen.has(id)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.duplicate_id',
        message: `Duplicate itemUiActions id '${id}'`,
        path: `${path}.id`,
      });
    }
    seen.add(id);

    const nodeIds = new Set<string>();
    next = collectNodeIds(entry.listContextMenu as MenuTreeNode[] | undefined, nodeIds, next, `${path}.listContextMenu`);
    next = collectNodeIds(entry.containdContextMenu as MenuTreeNode[] | undefined, nodeIds, next, `${path}.containdContextMenu`);
    next = collectNodeIds(entry.placeholderContextMenu as MenuTreeNode[] | undefined, nodeIds, next, `${path}.placeholderContextMenu`);

    const listActions = new Set<string>();
    const containdActions = new Set<string>();
    const phActions = new Set<string>();
    collectBuiltinActionIds(entry.listContextMenu as MenuTreeNode[] | undefined, listActions);
    collectBuiltinActionIds(entry.containdContextMenu as MenuTreeNode[] | undefined, containdActions);
    collectBuiltinActionIds(entry.placeholderContextMenu as MenuTreeNode[] | undefined, phActions);

    if (entry.listContextMenu !== undefined) {
      next = warnMissingBasics(next, listActions, LIST_MENU_BASICS, `${path}.listContextMenu`, 'List');
    }
    if (entry.containdContextMenu !== undefined) {
      next = warnMissingBasics(next, containdActions, CONTAIND_MENU_BASICS, `${path}.containdContextMenu`, 'Containd');
    }
    if (entry.placeholderContextMenu !== undefined) {
      next = warnMissingBasics(next, phActions, PLACEHOLDER_MENU_BASICS, `${path}.placeholderContextMenu`, 'Placeholder');
    }

    const allActions = new Set([...listActions, ...containdActions, ...phActions]);
    if (allActions.size > 0) {
      next = warnMissingDelete(next, allActions, path);
    }
  });

  return next;
}

export function validateElementItemUiActionsId(
  element: { itemUiActionsId?: string },
  elementPath: string,
  catalogIds: ReadonlySet<string>,
  result: ValidationResult,
): ValidationResult {
  const defaultsId = element.itemUiActionsId;
  if (defaultsId === undefined) return result;
  if (typeof defaultsId !== 'string' || defaultsId.trim() === '') {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'itemUiActionsId must be a non-empty string when present',
      path: `${elementPath}.itemUiActionsId`,
    });
  }
  if (!catalogIds.has(defaultsId)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.unresolved_reference',
      message: `Element references unknown itemUiActionsId '${defaultsId}'`,
      path: `${elementPath}.itemUiActionsId`,
    });
  }
  return result;
}

export function validateElementItemUiActionsRefs(
  elements: unknown[],
  catalogIds: ReadonlySet<string>,
  result: ValidationResult,
): ValidationResult {
  let next = result;
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (!isPlainObject(element)) continue;
    next = validateElementItemUiActionsId(
      element as { itemUiActionsId?: string },
      `elements[${i}]`,
      catalogIds,
      next,
    );
  }
  return next;
}
